import React from 'react';
import { formatVariance } from '../utils/formatters';

interface HistoryBadgeProps {
  current: number;
  previous: number | null;
  label?: string; // e.g. "since last sync"
  /** If true, show a neutral "—" when there's no previous value */
  showNeutral?: boolean;
}

/**
 * Small inline indicator of change vs a previous value.
 * Green up-arrow for increase, red down-arrow for decrease, neutral dash if no history.
 */
export const HistoryBadge: React.FC<HistoryBadgeProps> = ({ current, previous, label, showNeutral = true }) => {
  if (previous === null || previous === undefined) {
    if (!showNeutral) return null;
    return (
      <span className="inline-flex items-center gap-1 text-xs text-text-muted" title={label || 'No historical data'}>
        <span>—</span>
      </span>
    );
  }

  const delta = current - previous;
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-text-muted" title={label}>
        <span>=</span>
      </span>
    );
  }

  const up = delta > 0;
  const color = up ? 'text-green-400' : 'text-red-400';
  const arrow = up ? '↑' : '↓';

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`} title={label}>
      <span>{arrow}</span>
      <span>{formatVariance(delta)}</span>
    </span>
  );
};
